import { apiPost, apiGet } from './client';


export type SubscriberContactListRow = {
  contactListId: number;
  name: string;
  contactsCount?: number | null;
  status?: string;
};

/** Параметры страницы, как у POST …/subscribers / subscribers/view (count/offset/search…). */
export type SubscriberListViewQuery = {
  search?: string;
  count?: number;
  offset?: number;
  direction?: string;
  order?: string;
};

/** Сырой ответ POST …/front-api/subscribers (списки или контакты внутри списка). */
type SubscribersPostResponse = Record<string, unknown> & {
  contacts?: unknown[];
  totalLists?: number;
  totalPages?: number;
};

const LIST_PREVIEW_LIMIT = 50;

/** Кэш каталога списков: прогрев при входе и дедуп между экранами кампаний. */
const SUBSCRIBER_LISTS_CACHE_MS = 90_000;
let subscriberListsCache: { at: number; rows: SubscriberContactListRow[] } | null = null;
let subscriberListsInflight: Promise<SubscriberContactListRow[]> | null = null;

/** Сброс после изменения списков во внешнем SMS-сервисе (можно вызвать из UI). */
export function invalidateSubscriberContactListsCache(): void {
  subscriberListsCache = null;
}

/** Лёгкий прогрев при монтировании приложения (ошибки игнорируются). */
export function prefetchSubscriberContactLists(): Promise<void> {
  return fetchSubscriberContactListsAll()
    .then(() => undefined)
    .catch(() => undefined);
}

const VIEW_CACHE_MS = 45_000;
const subscriberViewResultCache = new Map<string, { at: number; body: unknown }>();
const subscriberViewInflight = new Map<string, Promise<unknown>>();

function subscriberViewCacheKey(contactListId: number, q: SubscriberListViewQuery): string {
  const search = q.search ?? '';
  const count = q.count ?? 10;
  const offset = q.offset ?? 0;
  const direction = q.direction ?? 'asc';
  const order = q.order ?? 'id';
  return `${contactListId}|${search}|${count}|${offset}|${direction}|${order}`;
}

/** Строка ответа SMS-сервиса (контакт внутри списка). */
export type SubscriberCabinetRecord = Record<string, unknown>;

function normalizeSubscribersContacts(body: unknown): unknown[] {
  if (!body || typeof body !== 'object') return [];
  const o = body as Record<string, unknown>;
  const c = o.contacts;
  if (Array.isArray(c)) return c;
  const d = o.data;
  if (Array.isArray(d)) return d;
  return [];
}

function readTotalHint(chunk: Record<string, unknown>): number | undefined {
  return readNumeric(chunk, [
    'totalContacts',
    'total_contact',
    'total',
    'contactsTotal',
    'recordsTotal',
    'filteredTotal',
    'contacts_count',
  ]);
}

function readNumeric(obj: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && /^\d+$/.test(v.trim())) return parseInt(v.trim(), 10);
  }
  return undefined;
}

/** Одна строка описания колонки/поля из POST …/getparameters — формат провайдера может различаться. */
function strField(v: unknown): string {
  if (v == null) return '';
  const s = String(v).trim();
  return s;
}

/** Человекочитаемые подписи полей контакта в списках SMS-сервиса (из ответа getparameters). */
export function extractSubscriberParameterLabels(body: unknown): string[] {
  if (!body || typeof body !== 'object') return [];
  const o = body as Record<string, unknown>;
  const raw = o.parameters ?? o.fields ?? o.columns;
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const entries = Object.entries(raw as Record<string, unknown>);
    for (const [k, v] of entries) {
      if (v != null && typeof v === 'object' && !Array.isArray(v)) {
        list.push(v);
      } else if (typeof v === 'string' && v.trim()) {
        list.push({ key: k, title: v });
      } else {
        list.push({ key: k, name: k });
      }
    }
  }
  const dataFallback = o.data;
  if (list.length === 0 && Array.isArray(dataFallback)) {
    list = dataFallback;
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of list) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const row = item as Record<string, unknown>;
    const label =
      strField(row.title) ||
      strField(row.label) ||
      strField(row.humanReadable) ||
      strField(row.name) ||
      strField(row.field) ||
      strField(row.parameterName) ||
      strField(row.columnName) ||
      strField(row.column) ||
      strField(row.key);
    const keyNorm = label.toLowerCase();
    if (label && !seen.has(keyNorm)) {
      seen.add(keyNorm);
      out.push(label);
    }
  }
  return out;
}

export async function fetchSubscribersFieldLabels(): Promise<string[]> {
  const body = await apiGet<unknown>('/api/sms/subscribers-parameters');
  return extractSubscriberParameterLabels(body);
}

/** Поля списка из POST …/subscribers/view/{id} (`titles`). */
export type SubscriberListViewFieldChip = {
  key: string;
  display: string;
};

function strFieldLoose(v: unknown): string {
  if (v == null) return '';
  const s = String(v).trim();
  return s;
}

/**
 * Плоские поля записи контакта: провайдер может отдать params, data, fields[] или скаляры на верхнем уровне.
 */
export function flattenSubscriberViewContactParams(item: unknown): Record<string, string> {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return {};
  const o = item as Record<string, unknown>;
  const out: Record<string, string> = {};

  const mergeFlat = (raw: unknown) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (v == null) out[k] = '';
      else if (Array.isArray(v)) out[k] = v.map((x) => (x == null ? '' : String(x))).join(', ');
      else if (typeof v === 'object') out[k] = JSON.stringify(v);
      else out[k] = String(v);
    }
  };

  mergeFlat(o.params);
  mergeFlat(o.data);

  const fieldsRaw = o.fields;
  if (Array.isArray(fieldsRaw)) {
    for (const f of fieldsRaw) {
      if (!f || typeof f !== 'object' || Array.isArray(f)) continue;
      const fr = f as Record<string, unknown>;
      const k =
        strFieldLoose(fr.key) ||
        strFieldLoose(fr.name) ||
        strFieldLoose(fr.field) ||
        strFieldLoose(fr.column) ||
        strFieldLoose(fr.parameterName);
      if (!k) continue;
      const val = fr.value ?? fr.val ?? fr.text ?? fr.content ?? '';
      out[k] = val == null ? '' : String(val);
    }
  }

  for (const [k, v99] of Object.entries(o)) {
    if (['params', 'data', 'fields', 'id', 'contactId', 'subscriberId'].includes(k)) continue;
    if (out[k] !== undefined) continue;
    if (v99 != null && typeof v99 !== 'object') {
      out[k] = String(v99);
    }
  }

  return out;
}

/** Разбор `titles`: сырой map key → подпись; дополняем ключами из contacts (если в titles не все колонки). */
export function readSubscriberViewTitlesMap(body: unknown): Record<string, string> {
  if (!body || typeof body !== 'object') return {};
  const o = body as Record<string, unknown>;
  const titles = o.titles;
  const out: Record<string, string> = {};

  if (titles && typeof titles === 'object' && !Array.isArray(titles)) {
    for (const [k, v] of Object.entries(titles as Record<string, unknown>)) {
      out[k] = String(v ?? '').trim() || k;
    }
  } else if (Array.isArray(titles)) {
    for (const item of titles) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const row = item as Record<string, unknown>;
      const key =
        strFieldLoose(row.key) ||
        strFieldLoose(row.name) ||
        strFieldLoose(row.field) ||
        strFieldLoose(row.column) ||
        strFieldLoose(row.parameterName);
      if (!key) continue;
      const label =
        strFieldLoose(row.title) ||
        strFieldLoose(row.label) ||
        strFieldLoose(row.humanReadable) ||
        key;
      out[key] = label || key;
    }
  }

  const raw = o.contacts;
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const flat = flattenSubscriberViewContactParams(item);
      for (const k of Object.keys(flat)) {
        if (!(k in out)) out[k] = k;
      }
    }
  }

  return out;
}

/** Чипы полей из уже собранной карты подписей. */
export function fieldChipsFromTitlesMap(titles: Record<string, string>): SubscriberListViewFieldChip[] {
  return Object.entries(titles)
    .map(([key, label]) => {
      const display = label === key ? label : `${label} (${key})`;
      return { key, display };
    })
    .sort((a, b) => a.key.localeCompare(b.key, 'en'));
}

/** Разбор `titles`: ключ → человекочитаемая подпись (как в UI провайдера). */
export function fieldChipsFromSubscriberViewTitles(body: unknown): SubscriberListViewFieldChip[] {
  return fieldChipsFromTitlesMap(readSubscriberViewTitlesMap(body));
}

export type SubscriberViewContactPreview = {
  id: string;
  params: Record<string, string>;
};

function parseSubscriberViewContactRow(item: unknown): SubscriberViewContactPreview | null {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const o = item as Record<string, unknown>;
  const id =
    (o.id != null ? String(o.id).trim() : '') ||
    (o.contactId != null ? String(o.contactId).trim() : '') ||
    (o.subscriberId != null ? String(o.subscriberId).trim() : '');
  const params = flattenSubscriberViewContactParams(item);
  if (!id && Object.keys(params).length === 0) return null;
  return { id: id || '—', params };
}

/** Строки из `contacts`; при `maxRows === undefined` — все строки текущей страницы ответа. */
export function extractSubscriberViewContactsPreview(
  body: unknown,
  maxRows?: number,
): { rows: SubscriberViewContactPreview[]; pageContactCount: number } {
  if (!body || typeof body !== 'object') return { rows: [], pageContactCount: 0 };
  const raw = (body as Record<string, unknown>).contacts;
  if (!Array.isArray(raw)) return { rows: [], pageContactCount: 0 };
  const rows: SubscriberViewContactPreview[] = [];
  for (const item of raw) {
    if (maxRows !== undefined && rows.length >= maxRows) break;
    const p = parseSubscriberViewContactRow(item);
    if (p) rows.push(p);
  }
  return { rows, pageContactCount: raw.length };
}

/** Число записей для MUI TablePagination: приоритет `totalContacts`; иначе оценка по обрезанной странице. */
export function subscriberViewPaginationCount(sec: {
  totalContacts?: number;
  page: number;
  rowsPerPage: number;
  contactPreview: { pageContactCount: number };
}): number {
  const len = sec.contactPreview.pageContactCount;
  const floor = sec.page * sec.rowsPerPage + len;
  if (sec.totalContacts != null && Number.isFinite(sec.totalContacts)) {
    return Math.max(sec.totalContacts, floor);
  }
  return floor + (len >= sec.rowsPerPage ? sec.rowsPerPage : 0);
}

/** Пары подпись/значение для отображения params (подпись как у чипов по titles). */
export function subscriberViewParamsDisplayRows(
  params: Record<string, string>,
  titlesMap: Record<string, string>,
): Array<{ key: string; label: string; value: string }> {
  const keys = Object.keys(params).sort((a, b) => a.localeCompare(b, 'en'));
  return keys.map((key) => {
    const human = (titlesMap[key] ?? '').trim() || key;
    const label = human === key ? human : `${human} (${key})`;
    return { key, label, value: params[key] ?? '' };
  });
}

export function readSubscriberViewListName(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const n = (body as Record<string, unknown>).name;
  if (typeof n !== 'string') return undefined;
  const s = n.trim();
  return s || undefined;
}

export function readSubscriberViewTotalContacts(body: unknown): number | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const v = (body as Record<string, unknown>).totalContacts;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) return parseInt(v.trim(), 10);
  return undefined;
}

/** Прокси POST …/front-api/subscribers/view/{id}; кэш ~45 с по ключу страницы + дедуп. */
export async function fetchSubscriberListView(
  contactListId: number,
  opts: SubscriberListViewQuery = {},
): Promise<unknown> {
  const {
    search = '',
    count = 10,
    offset = 0,
    direction = 'asc',
    order = 'id',
  } = opts;
  const q: SubscriberListViewQuery = { search, count, offset, direction, order };
  const key = subscriberViewCacheKey(contactListId, q);
  const now = Date.now();
  const hit = subscriberViewResultCache.get(key);
  if (hit && now - hit.at < VIEW_CACHE_MS) return hit.body;
  const inflight = subscriberViewInflight.get(key);
  if (inflight) return inflight;
  const p = apiPost<unknown>(`/api/sms/subscribers/view/${contactListId}`, {
    search,
    count,
    offset,
    direction,
    order,
  })
    .then((body) => {
      subscriberViewResultCache.set(key, { at: Date.now(), body });
      return body;
    })
    .finally(() => {
      subscriberViewInflight.delete(key);
    });
  subscriberViewInflight.set(key, p);
  return p;
}

export function formatSubscriberCabinetPreviewLine(rec: SubscriberCabinetRecord): string {
  const str = (v: unknown) => (v != null ? String(v).trim() : '');
  const pick = (...paths: string[]) => {
    for (const p of paths) {
      const s = str(rec[p]);
      if (s) return s;
    }
    return '';
  };
  const phone = pick('phone', 'phoneNumber', 'msisdn', 'mobile', 'number', 'tel', 'phone_number');
  const name = pick('name', 'firstName', 'lastName', 'fullname', 'fullName', 'contactName', 'title');
  const email = pick('email', 'e_mail', 'mail');
  const parts = [name, phone, email].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  const id = pick('id', 'contactId', 'contact_id');
  return id || JSON.stringify(rec);
}

/** POST /api/sms/subscribers — одна страница (как front-api/subscribers). */
export async function fetchSubscribersPage(opts: {
  search?: string;
  count?: number;
  offset?: number;
  direction?: string;
  order?: string;
  contactListId?: number;
}): Promise<SubscribersPostResponse> {
  return apiPost<SubscribersPostResponse>('/api/sms/subscribers', {
    search: opts.search ?? '',
    count: opts.count ?? 100,
    offset: opts.offset ?? 0,
    direction: opts.direction ?? 'asc',
    order: opts.order ?? 'id',
    contactListId: opts.contactListId ?? 0,
  });
}

/** Первые записи контактов из выбранного списка (contactListId > 0). */
export async function fetchSubscriberListContentsPreview(contactListId: number): Promise<{
  records: SubscriberCabinetRecord[];
  /** Если SMS-сервис прислал итог; иначе `undefined`. */
  totalHint?: number;
  /** true — возможно есть ещё страницы. */
  mayHaveMore: boolean;
}> {
  const chunk = await fetchSubscribersPage({
    contactListId,
    count: LIST_PREVIEW_LIMIT,
    offset: 0,
    order: 'id',
    direction: 'asc',
  });
  const raw = normalizeSubscribersContacts(chunk);
  const records = raw.filter(
    (x): x is SubscriberCabinetRecord =>
      x !== null && typeof x === 'object' && !Array.isArray(x),
  );
  const totalHint = readTotalHint(chunk as Record<string, unknown>);
  const mayHaveMore =
    records.length >= LIST_PREVIEW_LIMIT ||
    (totalHint !== undefined && totalHint > records.length);
  return {
    records,
    totalHint,
    mayHaveMore,
  };
}

async function fetchSubscriberContactListsAllFromApi(): Promise<SubscriberContactListRow[]> {
  const pageSize = 100;
  let offset = 0;
  const out: SubscriberContactListRow[] = [];
  for (;;) {
    const chunk = await fetchSubscribersPage({ count: pageSize, offset });
    const contacts = normalizeSubscribersContacts(chunk);
    for (const row of contacts) {
      if (
        row &&
        typeof row === 'object' &&
        !Array.isArray(row) &&
        'contactListId' in row
      ) {
        out.push(row as SubscriberContactListRow);
      }
    }
    if (contacts.length < pageSize) break;
    offset += contacts.length;
    if (offset > 50000) break;
  }
  return out;
}

/** Все списки контактов (добор страниц по размеру contacts). Кэш ~90 с, дедуп параллельных вызовов. */
export async function fetchSubscriberContactListsAll(): Promise<SubscriberContactListRow[]> {
  const now = Date.now();
  if (subscriberListsCache && now - subscriberListsCache.at < SUBSCRIBER_LISTS_CACHE_MS) {
    return subscriberListsCache.rows;
  }
  if (subscriberListsInflight) return subscriberListsInflight;
  subscriberListsInflight = (async () => {
    const rows = await fetchSubscriberContactListsAllFromApi();
    subscriberListsCache = { at: Date.now(), rows };
    return rows;
  })().finally(() => {
    subscriberListsInflight = null;
  });
  return subscriberListsInflight;
}
